import React, { useState, useEffect, useCallback } from 'react';
import { pipelineAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useUIStore } from '../../stores/uiStore';
import Modal from '../common/Modal';

interface TeamMember {
  user_id: string;
  email: string;
  full_name: string | null;
  platform_role: string | null;
  project_role: string;
  structure_masked: boolean;
  added_at: string | null;
}

interface Candidate {
  id: string;
  email: string;
  full_name: string | null;
  user_type: string;
}

interface TeamPanelProps {
  projectId: number;
}

const PROJECT_ROLES = ['leader', 'contributor', 'reviewer', 'observer'] as const;
type ProjectRole = typeof PROJECT_ROLES[number];

const ROLE_LABELS: Record<ProjectRole, string> = {
  leader: 'Leader',
  contributor: 'Contributor',
  reviewer: 'Reviewer',
  observer: 'Observer',
};

const ROLE_COLORS: Record<ProjectRole, string> = {
  leader: 'bg-teal-100 text-teal-700',
  contributor: 'bg-blue-100 text-blue-700',
  reviewer: 'bg-amber-100 text-amber-700',
  observer: 'bg-gray-100 text-gray-600',
};

/** Which roles are blocked for each user_type, with explanation */
const ROLE_RESTRICTIONS: Record<string, Partial<Record<ProjectRole, string>>> = {
  guest: {
    leader: 'Guests cannot be project leaders',
    contributor: 'Guests cannot be contributors',
  },
  trial: {
    leader: 'Trial members cannot lead projects',
  },
};

function getInitials(name: string | null, email: string): string {
  if (name) {
    return name
      .split(' ')
      .map((w) => w[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  }
  return email[0].toUpperCase();
}

const AVATAR_COLORS = [
  'bg-teal-500', 'bg-blue-500', 'bg-indigo-500', 'bg-purple-500',
  'bg-pink-500', 'bg-rose-500', 'bg-orange-500', 'bg-emerald-500',
];

function avatarColor(id: string): string {
  let hash = 0;
  for (let i = 0; i < id.length; i++) hash = ((hash << 5) - hash + id.charCodeAt(i)) | 0;
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

const TeamPanel: React.FC<TeamPanelProps> = ({ projectId }) => {
  const { user } = useAuth();
  const showNotification = useUIStore((s) => s.showNotification);

  const [members, setMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState<string | null>(null);

  const currentUserType = user?.user_type || 'member';
  const currentUserId = user?.id;

  const isLeaderOrAdmin =
    currentUserType === 'admin' ||
    currentUserType === 'super_admin' ||
    members.some((m) => m.user_id === currentUserId && m.project_role === 'leader');

  const leaderCount = members.filter((m) => m.project_role === 'leader').length;

  const fetchTeam = useCallback(async () => {
    try {
      const res = await pipelineAPI.getTeam(projectId);
      setMembers(res.data);
    } catch {
      showNotification('error', 'Failed to load team');
    } finally {
      setLoading(false);
    }
  }, [projectId, showNotification]);

  useEffect(() => {
    setLoading(true);
    fetchTeam();
  }, [fetchTeam]);

  const handleRoleChange = async (userId: string, newRole: string) => {
    try {
      await pipelineAPI.updateTeamMember(projectId, userId, { role: newRole });
      showNotification('success', 'Role updated');
      fetchTeam();
    } catch {
      showNotification('error', 'Failed to update role');
    }
  };

  const handleMaskToggle = async (userId: string, masked: boolean) => {
    try {
      await pipelineAPI.updateTeamMember(projectId, userId, { structure_masked: masked });
      showNotification('success', masked ? 'Structures masked' : 'Structures visible');
      fetchTeam();
    } catch {
      showNotification('error', 'Failed to update masking');
    }
  };

  const handleRemove = async (userId: string) => {
    try {
      await pipelineAPI.removeTeamMember(projectId, userId);
      showNotification('success', 'Member removed');
      setConfirmRemove(null);
      fetchTeam();
    } catch {
      showNotification('error', 'Failed to remove member');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="w-5 h-5 border-2 border-teal-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div>
      {/* Header row */}
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wide">
          Team ({members.length})
        </h3>
        {isLeaderOrAdmin && (
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-teal-700 bg-teal-50 rounded-md hover:bg-teal-100 transition-colors"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add
          </button>
        )}
      </div>

      {members.length === 0 ? (
        <p className="text-sm text-gray-400 text-center py-4">No team members</p>
      ) : (
        <div className="space-y-1.5">
          {members.map((m) => {
            const displayName = m.full_name || m.email;
            const isLastLeader = m.project_role === 'leader' && leaderCount <= 1;

            return (
              <div
                key={m.user_id}
                className="flex items-center gap-2.5 p-2 rounded-lg hover:bg-gray-50 transition-colors group"
              >
                {/* Avatar */}
                <div
                  className={`w-7 h-7 rounded-full flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0 ${avatarColor(m.user_id)}`}
                >
                  {getInitials(m.full_name, m.email)}
                </div>

                {/* Name + role */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="text-sm font-medium text-gray-900 truncate">{displayName}</span>
                    {m.structure_masked && (
                      <span title="Structure masked" className="text-gray-400 flex-shrink-0">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-medium ${ROLE_COLORS[m.project_role as ProjectRole] || ROLE_COLORS.observer}`}>
                      {ROLE_LABELS[m.project_role as ProjectRole] || m.project_role}
                    </span>
                    {m.platform_role && m.platform_role !== 'member' && (
                      <span className="text-[10px] text-gray-400">{m.platform_role}</span>
                    )}
                  </div>
                </div>

                {/* Actions (visible to leader/admin) */}
                {isLeaderOrAdmin && (
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    {/* Role dropdown */}
                    <select
                      value={m.project_role}
                      onChange={(e) => handleRoleChange(m.user_id, e.target.value)}
                      className="text-[11px] border border-gray-200 rounded px-1 py-0.5 bg-white text-gray-700 focus:outline-none focus:ring-1 focus:ring-teal-500"
                    >
                      {PROJECT_ROLES.map((role) => (
                        <option key={role} value={role}>{ROLE_LABELS[role]}</option>
                      ))}
                    </select>

                    {/* Mask toggle (for reviewers) */}
                    {m.project_role === 'reviewer' && (
                      <button
                        onClick={() => handleMaskToggle(m.user_id, !m.structure_masked)}
                        title={m.structure_masked ? 'Unmask structures' : 'Mask structures'}
                        className={`p-1 rounded ${m.structure_masked ? 'text-amber-500 bg-amber-50' : 'text-gray-400 hover:text-gray-600'} transition-colors`}
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          {m.structure_masked ? (
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                          ) : (
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          )}
                        </svg>
                      </button>
                    )}

                    {/* Remove button */}
                    {isLastLeader ? (
                      <span title="Assign another leader first" className="p-1 text-gray-300 cursor-not-allowed">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </span>
                    ) : (
                      <button
                        onClick={() => setConfirmRemove(m.user_id)}
                        title="Remove member"
                        className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Remove confirmation */}
      {confirmRemove && (
        <Modal isOpen onClose={() => setConfirmRemove(null)} title="Remove Team Member" size="sm">
          <p className="text-sm text-gray-600 mb-4">
            Remove <strong>{members.find((m) => m.user_id === confirmRemove)?.full_name || 'this member'}</strong> from the project?
          </p>
          <div className="flex justify-end gap-2">
            <button
              onClick={() => setConfirmRemove(null)}
              className="px-3 py-1.5 text-sm text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() => handleRemove(confirmRemove)}
              className="px-3 py-1.5 text-sm text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors"
            >
              Remove
            </button>
          </div>
        </Modal>
      )}

      {/* Add Member modal */}
      {showAddModal && (
        <AddMemberModal
          projectId={projectId}
          onClose={() => setShowAddModal(false)}
          onAdded={() => {
            fetchTeam();
            setShowAddModal(false);
          }}
        />
      )}
    </div>
  );
};

/* ─── Add Member Modal ──────────────────────────────────────── */

interface AddMemberModalProps {
  projectId: number;
  onClose: () => void;
  onAdded: () => void;
}

const AddMemberModal: React.FC<AddMemberModalProps> = ({ projectId, onClose, onAdded }) => {
  const showNotification = useUIStore((s) => s.showNotification);

  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [selectedUser, setSelectedUser] = useState<Candidate | null>(null);
  const [selectedRole, setSelectedRole] = useState<ProjectRole>('contributor');
  const [structureMasked, setStructureMasked] = useState(false);

  const fetchCandidates = useCallback(async () => {
    try {
      const res = await pipelineAPI.getTeamCandidates(projectId, search || undefined);
      setCandidates(res.data);
    } catch {
      showNotification('error', 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [projectId, search, showNotification]);

  useEffect(() => {
    setLoading(true);
    const timer = setTimeout(fetchCandidates, search ? 300 : 0);
    return () => clearTimeout(timer);
  }, [fetchCandidates, search]);

  const restrictions = selectedUser ? (ROLE_RESTRICTIONS[selectedUser.user_type] || {}) : {};

  const handleSubmit = async () => {
    if (!selectedUser) return;
    setSubmitting(true);
    try {
      await pipelineAPI.addTeamMember(projectId, {
        user_id: selectedUser.id,
        role: selectedRole,
        structure_masked: structureMasked,
      });
      showNotification('success', `${selectedUser.full_name || selectedUser.email} added to team`);
      onAdded();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'Failed to add member';
      showNotification('error', msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} title="Add Team Member" size="md">
      {/* Step 1: Select user */}
      {!selectedUser ? (
        <div>
          <input
            type="text"
            placeholder="Search by name or email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent mb-3"
            autoFocus
          />

          {loading ? (
            <div className="flex items-center justify-center py-6">
              <div className="w-5 h-5 border-2 border-teal-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : candidates.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-6">
              {search ? 'No matching users' : 'All users are already on this team'}
            </p>
          ) : (
            <div className="max-h-64 overflow-y-auto -mx-1">
              {candidates.map((c) => (
                <button
                  key={c.id}
                  onClick={() => {
                    setSelectedUser(c);
                    // Reset to a valid default role for this user type
                    const blocked = ROLE_RESTRICTIONS[c.user_type] || {};
                    if (blocked['contributor']) {
                      setSelectedRole('reviewer');
                    } else {
                      setSelectedRole('contributor');
                    }
                    setStructureMasked(false);
                  }}
                  className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg hover:bg-gray-50 transition-colors text-left"
                >
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold ${avatarColor(c.id)}`}>
                    {getInitials(c.full_name, c.email)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{c.full_name || c.email}</p>
                    {c.full_name && <p className="text-xs text-gray-400 truncate">{c.email}</p>}
                  </div>
                  {c.user_type !== 'member' && (
                    <span className="text-[10px] px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded">{c.user_type}</span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      ) : (
        /* Step 2: Configure role */
        <div>
          {/* Selected user header */}
          <div className="flex items-center gap-2.5 p-3 bg-gray-50 rounded-lg mb-4">
            <div className={`w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-bold ${avatarColor(selectedUser.id)}`}>
              {getInitials(selectedUser.full_name, selectedUser.email)}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900">{selectedUser.full_name || selectedUser.email}</p>
              {selectedUser.full_name && <p className="text-xs text-gray-400">{selectedUser.email}</p>}
            </div>
            <button
              onClick={() => setSelectedUser(null)}
              className="text-xs text-teal-600 hover:text-teal-700 font-medium"
            >
              Change
            </button>
          </div>

          {/* Role selection */}
          <label className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2 block">Project Role</label>
          <div className="grid grid-cols-2 gap-2 mb-4">
            {PROJECT_ROLES.map((role) => {
              const blocked = restrictions[role];
              const isSelected = selectedRole === role;
              return (
                <button
                  key={role}
                  disabled={!!blocked}
                  onClick={() => setSelectedRole(role)}
                  title={blocked || ''}
                  className={`relative px-3 py-2 rounded-lg border text-sm font-medium transition-all ${
                    blocked
                      ? 'border-gray-100 bg-gray-50 text-gray-300 cursor-not-allowed'
                      : isSelected
                      ? 'border-teal-500 bg-teal-50 text-teal-700 ring-1 ring-teal-500'
                      : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {ROLE_LABELS[role]}
                  {blocked && (
                    <span className="absolute -top-1 -right-1 w-4 h-4 bg-gray-300 rounded-full flex items-center justify-center">
                      <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Structure masking (only meaningful for reviewer) */}
          {selectedRole === 'reviewer' && (
            <label className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg mb-4 cursor-pointer">
              <input
                type="checkbox"
                checked={structureMasked}
                onChange={(e) => setStructureMasked(e.target.checked)}
                className="w-4 h-4 text-teal-600 rounded focus:ring-teal-500"
              />
              <div>
                <span className="text-sm font-medium text-amber-800">Mask chemical structures</span>
                <p className="text-xs text-amber-600 mt-0.5">Reviewer will see data but not structural drawings</p>
              </div>
            </label>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-2">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="px-4 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50"
            >
              {submitting ? 'Adding...' : 'Add to Team'}
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default TeamPanel;
